import { useMemo, useState, type CSSProperties } from 'react';
import { extractApiError } from '@/api/client';
import { Input, Select } from '@/components/Form';
import { Icon, type IconName } from '@/components/Icon';
import { useAuthStore } from '@/stores/auth';
import { canEditAny } from '@/permissions';
import { SheetGrid } from './SheetGrid';
import { SheetNewRowBar } from './SheetNewRowBar';
import { DRAFT, agoText, describeEdit, useSheetMirrorEditor } from './useSheetMirrorEditor';
import {
  sortSheetRows,
  type SheetChipColors,
  type SheetSort,
  type SheetWidths,
} from './sheetTable';

// BUSINESS '데모-계약 전환'(2026-10-23) — 오너: "데모-계약 전환이 제일 중요한 데이터".
// 구글 시트 '인바운드 문의 현황' 의 '데모-계약 전환('25 7월~)' 탭을 그대로 보여 주고 고친다.
//   · 칸 · 순서 · 드롭다운은 시트 그대로(스크립트가 시트에서 읽어 온다). 정본은 시트다.
//   · 칸에서 바로 고친다(SheetGrid — 오너 "그냥 스프레드 시트에 입력하는거마냥").
//   · 고친 칸 · 새 줄은 1분 안에 시트에 쓰인다. 그사이 시트에서 같은 칸이 바뀌었으면 쓰지 않고 알린다.
//   · 시트에서 고친 것은 1분 안에 여기에 보인다.

const TAB = 'demo-conversion' as const;

/**
 * 칸 기본 폭(px) — 2026-10-23 오너가 화면에서 맞춘 폭("지금 내가 맞춰둔 너비를 기본 너비로").
 * 여기 없는 칸(시트에 새로 생긴 칸)은 시트 열 너비. 머리글 끝을 두 번 누르면 이 폭으로 돌아간다.
 */
const DEFAULT_WIDTHS: SheetWidths = {
  'IB 날짜': 54,
  '시연 날짜': 54,
  상담원: 72,
  상호: 126,
  '업장 위치': 40,
  연락처: 104,
  '신규 창업': 40,
  '데모 반응 (추정)': 209,
  Status: 88,
  'Sura 관심도': 44,
  '현장 상담/실측': 56,
  '예상 대수': 40,
  '계약 예정': 40,
  '계약 확정': 40,
  '계약 기간': 40,
  비고: 309,
};

/**
 * 드롭다운 칩 색 — 사본 시트의 드롭다운 설정과 같게(2026-10-23 확인). 구글이 칩 색을 스크립트에 주지 않아
 * 여기 적어 둔다. 시트에서 칩 색을 바꾸거나 상담원이 늘면 여기도 고친다(없는 값은 회색 칩).
 */
const CHIP_COLORS: SheetChipColors = {
  상담원: {
    Jake: ['#11734b', '#ffffff'],
    Toni: ['#b10202', '#ffffff'],
    Andy: ['#0a53a8', '#ffffff'],
    Dylan: ['#5a3286', '#ffffff'],
    Kai: ['#ffcfc9', '#b10202'],
    Ade: ['#753800', '#ffffff'],
  },
  '데모 반응 (추정)': {
    '1-*': ['#d4edbc', '#11734b'],
    '2-*': ['#ffe5a0', '#473821'],
    '3-*': ['#ffcfc9', '#b10202'],
  },
  Status: {
    '1-확정': ['#d4edbc', '#11734b'],
    '2-직전': ['#ffe5a0', '#473821'],
    '3-리탭필요': ['#bfe1f6', '#0a53a8'],
    '4-보류': ['#e6e6e6', '#3d3d3d'],
    '5-아웃': ['#ffcfc9', '#b10202'],
  },
};

/** 긴 글 칸 — 표 맨 끝, 왼쪽 정렬, 칸 아래로 펼쳐 쓴다. */
const LONG_KEYS = new Set(['비고']);

export function DemoConversionPage() {
  const role = useAuthStore((s) => s.user?.role);
  const canEdit = !!role && canEditAny(role);
  const { q, tab, draft, setDraft, problems, commit, addMx, buildRows, withPending, undo, redo } =
    useSheetMirrorEditor(TAB);

  const [search, setSearch] = useState('');
  const [agent, setAgent] = useState('');
  const [status, setStatus] = useState('');
  const [reaction, setReaction] = useState('');
  /** 머리글을 눌러 고른 정렬 — 콘솔에서만(시트 순서는 그대로) */
  const [sort, setSort] = useState<SheetSort>(null);
  /** 인바운드 현황의 중복 상대로 들어왔을 때(?hl=줄 열쇠) — 그 줄을 강조 */
  const [hlRow] = useState(() => new URLSearchParams(window.location.search).get('hl'));

  const columns = tab?.columns ?? [];
  const col = (k: string) => columns.find((c) => c.key === k);
  const statusCol = col('Status');
  const agentCol = col('상담원');
  const reactionCol = columns.find((c) => c.key.startsWith('데모 반응'));

  /** 표 칸 — 숨긴 칸은 빼고, 긴 글 칸(비고)은 맨 끝 */
  const gridCols = useMemo(() => {
    const cols = tab?.columns ?? [];
    return [
      ...cols.filter((c) => !c.hidden && !LONG_KEYS.has(c.key)),
      ...cols.filter((c) => LONG_KEYS.has(c.key)),
    ];
  }, [tab?.columns]);

  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    const list = (tab?.rows ?? []).filter((r) => {
      if (
        agent &&
        !(r.cells['상담원'] ?? '')
          .split(',')
          .map((x) => x.trim())
          .includes(agent)
      )
        return false;
      if (status && (r.cells['Status'] ?? '') !== status) return false;
      if (reaction && reactionCol && (r.cells[reactionCol.key] ?? '') !== reaction) return false;
      if (s && !Object.values(r.cells).some((v) => v.toLowerCase().includes(s))) return false;
      return true;
    });
    // 기본은 최근 줄 먼저(시트 아래쪽부터) — 머리글을 눌러 다른 순서로(2026-10-23 오너 체크박스 뺌)
    return sortSheetRows(withPending([...list].reverse()), sort);
  }, [tab, search, agent, status, reaction, reactionCol, sort, withPending]);

  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of tab?.rows ?? []) {
      const v = r.cells['Status'] || '(비어 있음)';
      m.set(v, (m.get(v) ?? 0) + 1);
    }
    return m;
  }, [tab]);
  const sum = (k: string) =>
    (tab?.rows ?? []).reduce(
      (acc, r) => acc + (Number(String(r.raw[k] ?? r.cells[k] ?? '').replace(/,/g, '')) || 0),
      0,
    );

  const gridRows = useMemo(
    () =>
      buildRows(rows, gridCols, (r, values) => ({
        fill: isHot(values) ? HOT_FILL : undefined,
        mark: r.rowKey === hlRow,
      })),
    [buildRows, rows, gridCols, hlRow],
  );

  if (q.isLoading) return <div style={page}>불러오는 중…</div>;
  if (q.isError || !tab)
    return (
      <div style={page}>
        <h1 className="kuun-h2" style={{ margin: 0 }}>
          데모-계약 전환
        </h1>
        <div style={notice} data-testid="sheet-not-connected">
          {extractApiError(q.error)}
        </div>
      </div>
    );

  const stale = !tab.lastSyncAt || Date.now() - Date.parse(tab.lastSyncAt) > 5 * 60_000;
  const count = (
    <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
      {rows.length}줄
    </span>
  );
  // 표 왼쪽 위 [+ 새 줄] · 줄 수(2026-10-23 오너 "이건 + 새 줄 버튼 오른쪽에")
  const newRowBar = canEdit ? (
    <SheetNewRowBar
      drafting={!!draft}
      canAdd={!!(draft?.['상호'] ?? '').trim()}
      adding={addMx.isPending}
      count={count}
      hint={
        <>
          표 맨 위 줄의 칸을 채우고 [시트에 추가]를 눌러 주세요. &lsquo;상호&rsquo;는 꼭 넣어야
          합니다.
        </>
      }
      onStart={() => setDraft((d) => d ?? {})}
      onCancel={() => setDraft(null)}
      onAdd={() => draft && addMx.mutate(draft)}
    />
  ) : (
    count
  );

  return (
    <div style={page}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ marginRight: 'auto' }}>
          <h1 className="kuun-h2" style={{ margin: 0 }}>
            데모-계약 전환
          </h1>
          <div className="kuun-caption" style={{ color: 'var(--fg-muted)', marginTop: 4 }}>
            구글 시트 &lsquo;{tab.sheetName}&rsquo; 탭과 1분마다 주고받습니다. 시트에 {tab.rowCount}
            줄이 있습니다.{' '}
            <span
              style={stale ? { color: 'var(--fg-danger)' } : undefined}
              data-testid="sheet-sync-at"
            >
              {tab.lastSyncAt
                ? `마지막 동기화는 ${agoText(tab.lastSyncAt)}입니다.`
                : '아직 동기화하지 않았습니다.'}
            </span>
          </div>
        </div>
        <a href={tab.sheetUrl} target="_blank" rel="noreferrer" style={linkBtn}>
          시트 열기
        </a>
      </div>

      {tab.lastError && <div style={notice}>{tab.lastError}</div>}
      {problems.length > 0 && (
        <div style={notice} data-testid="sheet-edit-problems">
          {problems.slice(0, 5).map((e) => (
            <div key={e.id}>
              {describeEdit(e, tab)} 칸을 시트에 쓰지 못했습니다. {e.message}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {(statusCol?.options ?? [...counts.keys()]).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatus(status === s ? '' : s)}
            className="kuun-sheet-kpi"
            style={{ ...kpi, ...(status === s ? kpiOn : null) }}
            aria-pressed={status === s}
          >
            <span className="kuun-caption" style={kpiLabel} title={s}>
              <StatusMark status={s} />
              {stripNo(s)}
            </span>
            <strong style={{ fontSize: 18 }}>{counts.get(s) ?? 0}</strong>
          </button>
        ))}
        {col('예상 대수') && <Kpi icon="bot" label="예상 대수 합" value={sum('예상 대수')} />}
        {col('계약 확정') && <Kpi icon="file" label="계약 확정 대수 합" value={sum('계약 확정')} />}
      </div>

      {/* 새 줄 작성 중에는 [시트에 추가] · [취소] 가 검색 칸 자리로 올라오고, 끝나면 돌아간다(2026-10-23 오너) */}
      {draft && newRowBar ? (
        newRowBar
      ) : (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Input
            placeholder="상호, 연락처, 비고 검색"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 240 }}
            aria-label="검색"
          />
          {agentCol?.options && (
            <Select
              value={agent}
              onChange={(e) => setAgent(e.target.value)}
              aria-label="상담원"
              style={{ width: 140 }}
            >
              <option value="">상담원 전체</option>
              {agentCol.options.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </Select>
          )}
          {reactionCol?.options && (
            <Select
              value={reaction}
              onChange={(e) => setReaction(e.target.value)}
              aria-label="데모 반응"
              style={{ width: 220 }}
            >
              <option value="">데모 반응 전체</option>
              {reactionCol.options.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </Select>
          )}
        </div>
      )}

      <div style={tableBlock}>
        {!draft && newRowBar}
        <SheetGrid
          columns={gridCols}
          rows={gridRows}
          widths={DEFAULT_WIDTHS}
          colors={CHIP_COLORS}
          longKeys={LONG_KEYS}
          readOnly={!canEdit}
          onCommit={commit}
          focusRow={draft ? DRAFT : hlRow}
          sort={sort}
          onSort={setSort}
          onUndo={() => void undo()}
          onRedo={() => void redo()}
        />
      </div>
    </div>
  );
}

/** 숫자 칸 이름 옆 표시 — 글씨 크기만 하게(2026-10-23 오너 "글씨크기만한걸로") */
const MARK_SIZE = 12;

/**
 * Status 표시 — 시트 값 앞 번호('1-')로 고른다(이름이 조금 바뀌어도 번호는 그대로).
 * 색은 시트 칩 글자색 계열.
 */
const STATUS_MARKS: Record<string, readonly [IconName, string]> = {
  '1': ['check-circle', '#11734b'],
  '2': ['clock', '#b07d00'],
  '3': ['phone', '#0a53a8'],
  '4': ['pause', '#6b6b6b'],
  '5': ['x', '#b10202'],
};

function StatusMark({ status }: { status: string }) {
  const m = STATUS_MARKS[/^(\d+)\s*-/.exec(status)?.[1] ?? ''];
  return m ? <Icon name={m[0]} size={MARK_SIZE} color={m[1]} strokeWidth={2.25} /> : null;
}

/** '1-확정' → '확정' — 숫자 칸 이름에서만 번호를 뺀다(오너 "앞에 넘버링 빼고"). 시트 값 · 걸러 보기는 그대로. */
const stripNo = (s: string) => s.replace(/^\d+\s*-\s*/, '');

function Kpi({ icon, label, value }: { icon?: IconName; label: string; value: number }) {
  return (
    <div style={kpi}>
      <span className="kuun-caption" style={kpiLabel}>
        {icon && <Icon name={icon} size={MARK_SIZE} color="var(--fg-muted)" strokeWidth={2.25} />}
        {label}
      </span>
      <strong style={{ fontSize: 18 }}>{value}</strong>
    </div>
  );
}

/**
 * 시트처럼 줄 전체를 초록으로 — 데모 반응 '1-도입 의사 높음' 이고 Status 가 '1-확정' · '2-직전'(2026-10-23 오너).
 * 고치는 중인 값으로 따지므로 콘솔에서 바꾸면 곧바로 바뀐다.
 */
const HOT_FILL = '#d9ead3';
function isHot(values: Record<string, string>): boolean {
  const reactionKey = Object.keys(values).find((k) => k.startsWith('데모 반응'));
  const reaction = reactionKey ? (values[reactionKey] ?? '').trim() : '';
  const status = (values['Status'] ?? '').trim();
  return reaction.startsWith('1-') && (status.startsWith('1-') || status.startsWith('2-'));
}

const page: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  // 좌우는 레이아웃 여백(24)만 — 화면 여백이 겹쳐 48 이던 것을 반으로(2026-10-23 오너 "여기 여백을 반으로")
  // 여백은 레이아웃 여백(24)만 — 위쪽도 다른 화면과 같게(2026-10-23 오너 "윗쪽 간격도 다른 탭들에 비해 좀 큰거같은데")
  padding: 0,
  minWidth: 0,
};
/** [+ 새 줄] 과 표 — 표 바로 왼쪽 위에 붙여 둔다(2026-10-23 오너 "표 왼쪽 위에 보이게") */
const tableBlock: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 };
const notice: CSSProperties = {
  padding: '10px 14px',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-warning-subtle)',
  color: 'var(--fg-on-warning-tint)',
  fontSize: 13,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};
const kpi: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 2,
  minWidth: 110,
  padding: '8px 12px',
  borderRadius: 'var(--r-card)',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  cursor: 'pointer',
  textAlign: 'left',
};
const kpiOn: CSSProperties = {
  // 고른 칸은 옅은 바탕 + 얇은 테두리(2026-10-23 오너 "진한 테두리로 남는거 너무 이상").
  // borderColor 만 쓰면 풀 때 React 가 색만 지워 테두리가 글자색(검정)으로 남는다 — 테두리 전체를 준다.
  border: '1px solid color-mix(in srgb, var(--brand) 45%, var(--border-default))',
  background: 'color-mix(in srgb, var(--brand) 8%, var(--bg-surface))',
};
const kpiLabel: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  color: 'var(--fg-muted)',
};
const linkBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 32,
  padding: '0 14px',
  borderRadius: 'var(--r-btn)',
  border: '1px solid var(--border-default)',
  color: 'var(--fg-default)',
  textDecoration: 'none',
  fontSize: 13,
  fontWeight: 500,
};
